import type { FormEvent, InputHTMLAttributes } from "react"
import { Button } from "@/shared/ui/Button"
import { Disclaimer } from "@/shared/ui/Disclaimer"
import {
  CheckboxField,
  ChoiceGroup,
  FormField,
  RadioField,
  SelectField,
  TextAreaField,
} from "@/shared/ui/FormField"
import { UFS } from "@/shared/lib/brazil"
import { formatCpf, formatTaxId } from "@/shared/lib/documents"
import { formatPhone } from "@/shared/lib/phone"
import { formatPostalCode } from "@/shared/lib/postalCode"
import { ArrowRight } from "@/shared/ui/icons"
import {
  ACCOUNT_KINDS,
  ACCREDITATION_DOCUMENTS,
  ACCREDITATION_KINDS,
  ACCREDITATION_SERVICES,
  OTHER_OPTION_ID,
  type AccreditationFormValues,
} from "@/features/accreditation/formModel"
import { FormBlock } from "@/features/accreditation/components/FormBlock"
import { SubmitErrorPanel } from "@/features/accreditation/components/AccreditationFeedback"
import { fieldId, type useAccreditationForm } from "@/features/accreditation/useAccreditationForm"

export type AccreditationFormState = ReturnType<typeof useAccreditationForm>

/** Os campos da ficha que sao texto livre — os unicos que `Text` sabe escrever. */
type TextFieldName = Exclude<
  {
    [K in keyof AccreditationFormValues]: AccreditationFormValues[K] extends string ? K : never
  }[keyof AccreditationFormValues],
  "kind" | "open24h"
>

// "form" tambem sai: o atributo HTML de mesmo nome e uma string (o id de um
// <form>), e o nosso e o estado da ficha.
interface TextProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "value" | "onChange" | "id" | "form"> {
  form: AccreditationFormState
  field: TextFieldName
  label: string
  hint?: string
  optional?: boolean
  /** Formatador aplicado a cada tecla — CEP, telefone, CPF/CNPJ. */
  mask?: (value: string) => string
  fieldClassName?: string
}

/**
 * Um campo de texto ligado ao estado da ficha.
 *
 * Trinta e poucos campos escritos a mao seriam trinta e poucas oportunidades de
 * esquecer o `error`, o `id` ou o `onChange` — e o campo esquecido fica mudo,
 * sem erro de compilacao. Ligando por nome, o TypeScript recusa um campo que
 * nao existe na ficha.
 */
function Text({ form, field, label, mask, ...rest }: TextProps) {
  return (
    <FormField
      id={fieldId(field)}
      label={label}
      value={form.values[field]}
      onChange={(event) => form.setField(field, mask ? mask(event.target.value) : event.target.value)}
      error={form.errors[field]}
      {...rest}
    />
  )
}

interface LongTextProps {
  form: AccreditationFormState
  field: TextFieldName
  label: string
  placeholder?: string
  hint?: string
  optional?: boolean
  rows?: number
}

function LongText({ form, field, label, ...rest }: LongTextProps) {
  return (
    <TextAreaField
      id={fieldId(field)}
      label={label}
      value={form.values[field]}
      onChange={(event) => form.setField(field, event.target.value)}
      error={form.errors[field]}
      {...rest}
    />
  )
}

const GRID = "grid sm:grid-cols-2 gap-4"

export interface AccreditationFormProps {
  form: AccreditationFormState
}

export function AccreditationForm({ form }: AccreditationFormProps) {
  const { values, setField, toggleInList, errors } = form

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    void form.submit()
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-10">
      {/* ── 1. Tipo de credenciamento ───────────────────────────────────── */}
      <FormBlock number={1} title="Tipo de credenciamento">
        <ChoiceGroup
          id={fieldId("kind")}
          legend="Selecione o perfil que descreve o atendimento"
          error={errors.kind}
          columns={2}
        >
          {ACCREDITATION_KINDS.map((option) => (
            <RadioField
              key={option.id}
              id={`credenciamento-tipo-${option.id}`}
              name="credenciamento-tipo"
              label={option.label}
              checked={values.kind === option.id}
              onChange={() => setField("kind", option.id)}
            />
          ))}
        </ChoiceGroup>
      </FormBlock>

      <Divider />

      {/* ── 2. Dados do estabelecimento / profissional ──────────────────── */}
      <FormBlock number={2} title="Dados do estabelecimento / profissional">
        <div className={GRID}>
          <Text
            form={form}
            field="legalName"
            label="Razão social / Nome completo"
            placeholder="Como consta no CNPJ ou no documento"
            autoComplete="organization"
            fieldClassName="sm:col-span-2"
          />
          <Text
            form={form}
            field="tradeName"
            label="Nome fantasia"
            placeholder="Nome pelo qual a unidade é conhecida"
            optional
          />
          <Text
            form={form}
            field="taxId"
            label="CNPJ / CPF"
            placeholder="00.000.000/0000-00"
            inputMode="numeric"
            maxLength={18}
            mask={formatTaxId}
          />
          <Text
            form={form}
            field="crmv"
            label="CRMV"
            placeholder="CRMV-RN 0000"
            hint="Registro da pessoa jurídica ou do profissional autônomo."
          />
          <Text
            form={form}
            field="postalCode"
            label="CEP"
            placeholder="00000-000"
            inputMode="numeric"
            maxLength={9}
            autoComplete="postal-code"
            mask={formatPostalCode}
          />
          <Text
            form={form}
            field="address"
            label="Endereço"
            placeholder="Rua, número e complemento"
            autoComplete="street-address"
            fieldClassName="sm:col-span-2"
          />
          <Text form={form} field="district" label="Bairro" placeholder="Bairro" />
          <div className="grid grid-cols-[1fr_100px] gap-4">
            <Text form={form} field="city" label="Cidade" placeholder="Cidade" />

            <SelectField
              id={fieldId("state")}
              label="UF"
              value={values.state}
              onChange={(event) => setField("state", event.target.value)}
              error={errors.state}
            >
              <option value="">—</option>
              {UFS.map((uf) => (
                <option key={uf} value={uf}>
                  {uf}
                </option>
              ))}
            </SelectField>
          </div>

          <Text
            form={form}
            field="phone"
            label="Telefone"
            placeholder="(00) 0000-0000"
            inputMode="tel"
            autoComplete="tel"
            maxLength={15}
            mask={formatPhone}
          />
          <Text
            form={form}
            field="whatsapp"
            label="WhatsApp"
            placeholder="(00) 00000-0000"
            inputMode="tel"
            maxLength={15}
            mask={formatPhone}
            optional
          />
          <Text
            form={form}
            field="email"
            label="E-mail"
            type="email"
            placeholder="contato@suaclinica.com.br"
            autoComplete="email"
          />
          <Text
            form={form}
            field="socialMedia"
            label="Instagram / Redes sociais"
            placeholder="@suaclinica"
            optional
          />
        </div>
      </FormBlock>

      <Divider />

      {/* ── 3. Responsavel tecnico ──────────────────────────────────────── */}
      <FormBlock
        number={3}
        title="Responsável técnico"
        description="O médico-veterinário responsável pelo atendimento na unidade."
      >
        <div className={GRID}>
          <Text
            form={form}
            field="managerName"
            label="Nome completo"
            placeholder="Nome do responsável técnico"
            fieldClassName="sm:col-span-2"
          />
          <Text form={form} field="managerCrmv" label="CRMV" placeholder="CRMV-RN 0000" />
          <Text
            form={form}
            field="managerCpf"
            label="CPF"
            placeholder="000.000.000-00"
            inputMode="numeric"
            maxLength={14}
            mask={formatCpf}
          />
          <Text
            form={form}
            field="managerPhone"
            label="Telefone / WhatsApp"
            placeholder="(00) 00000-0000"
            inputMode="tel"
            maxLength={15}
            mask={formatPhone}
          />
          <Text
            form={form}
            field="managerEmail"
            label="E-mail"
            type="email"
            placeholder="responsavel@suaclinica.com.br"
          />
        </div>
      </FormBlock>

      <Divider />

      {/* ── 4. Servicos disponiveis ─────────────────────────────────────── */}
      <FormBlock number={4} title="Serviços disponíveis">
        <ChoiceGroup
          id={fieldId("services")}
          legend="Marque tudo o que a unidade oferece"
          error={errors.services}
          columns={3}
        >
          {ACCREDITATION_SERVICES.map((option) => (
            <CheckboxField
              key={option.id}
              id={`credenciamento-servico-${option.id}`}
              label={option.label}
              checked={values.services.includes(option.id)}
              onChange={(checked) => toggleInList("services", option.id, checked)}
            />
          ))}

          <CheckboxField
            id={`credenciamento-servico-${OTHER_OPTION_ID}`}
            label="Outros"
            checked={values.otherServicesChecked}
            onChange={(checked) => setField("otherServicesChecked", checked)}
          />
        </ChoiceGroup>

        {values.otherServicesChecked ? (
          <Text
            form={form}
            field="otherServices"
            label="Quais outros serviços"
            placeholder="Descreva os demais serviços disponíveis"
          />
        ) : null}
      </FormBlock>

      <Divider />

      {/* ── 5. Horario de funcionamento ─────────────────────────────────── */}
      <FormBlock number={5} title="Horário de funcionamento">
        <LongText
          form={form}
          field="openingHours"
          label="Dias e horários"
          placeholder="Seg. a sex., 8h às 18h · Sáb., 8h às 12h"
          rows={2}
        />

        <ChoiceGroup
          id={fieldId("open24h")}
          legend="Atendimento 24 horas"
          error={errors.open24h}
          columns={2}
        >
          <RadioField
            id="credenciamento-24h-sim"
            name="credenciamento-24h"
            label="Sim"
            checked={values.open24h === "sim"}
            onChange={() => setField("open24h", "sim")}
          />
          <RadioField
            id="credenciamento-24h-nao"
            name="credenciamento-24h"
            label="Não"
            checked={values.open24h === "nao"}
            onChange={() => setField("open24h", "nao")}
          />
        </ChoiceGroup>
      </FormBlock>

      <Divider />

      {/* ── 6. Condicoes de atendimento ─────────────────────────────────── */}
      <FormBlock
        number={6}
        title="Condições de atendimento DIMPET"
        description="Uma proposta inicial. Os valores são conversados antes da assinatura — nada aqui é definitivo."
      >
        <LongText
          form={form}
          field="negotiatedPrices"
          label="Valores / Tabela negociada"
          placeholder="Consulta, retorno, procedimentos e exames"
          optional
        />
        <LongText
          form={form}
          field="paymentTerms"
          label="Prazo e forma de pagamento"
          placeholder="Ex.: repasse em até 30 dias, por transferência"
          optional
          rows={2}
        />
        <LongText
          form={form}
          field="notes"
          label="Observações / Condições especiais"
          placeholder="Restrições de agenda, espécies atendidas, limites de procedimento"
          optional
        />
      </FormBlock>

      <Divider />

      {/* ── 7. Dados bancarios ──────────────────────────────────────────── */}
      <FormBlock
        number={7}
        title="Dados bancários para repasse"
        description="Bloco inteiro opcional. Se preferir, combine a conta depois da aprovação — o pedido é analisado sem ela."
      >
        <div className={GRID}>
          <Text form={form} field="bank" label="Banco" placeholder="Nome ou número do banco" optional />
          <Text form={form} field="bankBranch" label="Agência" placeholder="0000" optional />
          <Text form={form} field="bankAccount" label="Conta" placeholder="00000-0" optional />

          <SelectField
            id={fieldId("bankAccountKind")}
            label="Tipo de conta"
            value={values.bankAccountKind}
            onChange={(event) => setField("bankAccountKind", event.target.value)}
            optional
          >
            <option value="">Selecione</option>
            {ACCOUNT_KINDS.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </SelectField>

          <Text form={form} field="bankHolder" label="Titular" placeholder="Nome do titular" optional />
          <Text
            form={form}
            field="bankHolderTaxId"
            label="CPF / CNPJ do titular"
            placeholder="000.000.000-00"
            inputMode="numeric"
            maxLength={18}
            mask={formatTaxId}
            optional
          />
          <Text
            form={form}
            field="pixKey"
            label="Chave PIX"
            placeholder="CPF, CNPJ, e-mail, telefone ou chave aleatória"
            optional
            fieldClassName="sm:col-span-2"
          />
        </div>
      </FormBlock>

      <Divider />

      {/* ── 8. Documentacao ─────────────────────────────────────────────── */}
      <FormBlock
        number={8}
        title="Documentação para credenciamento"
        description="Marque o que já está em mãos. Os arquivos não sobem por aqui — vamos pedi-los na resposta a este pedido."
      >
        <ChoiceGroup
          id={fieldId("documents")}
          legend="Documentos disponíveis"
          columns={2}
          hint="Nenhum documento é obrigatório neste momento."
        >
          {ACCREDITATION_DOCUMENTS.map((option) => (
            <CheckboxField
              key={option.id}
              id={`credenciamento-documento-${option.id}`}
              label={option.label}
              checked={values.documents.includes(option.id)}
              onChange={(checked) => toggleInList("documents", option.id, checked)}
            />
          ))}

          <CheckboxField
            id={`credenciamento-documento-${OTHER_OPTION_ID}`}
            label="Outros documentos"
            checked={values.otherDocumentsChecked}
            onChange={(checked) => setField("otherDocumentsChecked", checked)}
          />
        </ChoiceGroup>

        {values.otherDocumentsChecked ? (
          <Text
            form={form}
            field="otherDocuments"
            label="Quais outros documentos"
            placeholder="Descreva os demais documentos disponíveis"
          />
        ) : null}
      </FormBlock>

      <Divider />

      {/* ── 9. Declaracao ───────────────────────────────────────────────── */}
      <FormBlock number={9} title="Declaração">
        <p className="text-sm text-verde-profundo/65 leading-relaxed bg-off-white rounded-xl p-4 border border-verde-profundo/8">
          Declaro que as informações prestadas nesta ficha são verdadeiras e que estou de acordo com as
          condições estabelecidas pela DIMPET para análise e eventual credenciamento na rede prestadora.
          Comprometo-me a manter atualizados os dados cadastrais e a documentação necessária para a prestação
          dos serviços veterinários.
        </p>

        <div className={GRID}>
          <Text
            form={form}
            field="declarationPlace"
            label="Local"
            placeholder="Cidade / UF"
            hint="A data do envio é registrada automaticamente."
          />
        </div>

        <CheckboxField
          id={fieldId("declarationAccepted")}
          label="Li e concordo com a declaração acima"
          description="A assinatura da ficha em papel é substituída por este aceite e pelo registro do envio."
          checked={values.declarationAccepted}
          onChange={(checked) => setField("declarationAccepted", checked)}
        />

        {errors.declarationAccepted ? (
          <p className="text-xs text-red-500 -mt-3">{errors.declarationAccepted}</p>
        ) : null}
      </FormBlock>

      {/* ── Envio ───────────────────────────────────────────────────────── */}
      <div className="flex flex-col gap-4">
        {form.status === "error" ? <SubmitErrorPanel message={form.submitError} /> : null}

        {form.showErrors && form.errorCount > 0 ? (
          <p className="text-sm text-red-600" role="alert">
            {form.errorCount === 1
              ? "Um campo precisa de atenção antes do envio."
              : `${form.errorCount} campos precisam de atenção antes do envio.`}
          </p>
        ) : null}

        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <Button
            variant="primary"
            size="lg"
            type="submit"
            loading={form.status === "submitting"}
            icon={<ArrowRight />}
          >
            Enviar ficha de credenciamento
          </Button>

          <p className="text-xs text-verde-profundo/45 leading-relaxed">
            Retornamos pelo e-mail informado no bloco 2.
          </p>
        </div>

        <Disclaimer>
          Os dados enviados são usados apenas para a análise deste pedido de credenciamento e para o contato
          decorrente dele. O envio não gera vínculo nem garante credenciamento — a rede é definida após
          análise da documentação e acordo comercial.
        </Disclaimer>
      </div>
    </form>
  )
}

function Divider() {
  return <hr className="border-0 border-t border-verde-profundo/8" />
}
